import React from 'react';
import { Dumbbell, TrendingUp, Star, Zap } from 'lucide-react';
import FeaturePage from './FeaturePage';

export default function Training() {
  return (
    <FeaturePage
      seo={{
        title: 'Player Training & Development - Improve Your Squad | My Football Game',
        description: 'Train your players to improve their stats in My Football Game. Develop young talents, boost key attributes and build a world-class squad through the training system.',
        path: '/features/training',
      }}
      eyebrow="Feature Spotlight"
      title="PLAYER "
      titleAccent="TRAINING"
      subtitle="Develop your players by training individual attributes. Young players grow faster, veterans bring experience, and smart training turns average squads into title contenders."
      breadcrumbLabel="Training"
      sections={[
        {
          icon: <Dumbbell className="w-5 h-5" />,
          title: 'Train Individual Stats',
          content: (
            <>
              <p>Select a player and choose which attribute to train: pace, shooting, passing, dribbling, defending, or physical. Each training session costs coins and improves the chosen stat, giving you control over how your players develop.</p>
              <p>Training is not random — you decide which stat improves. A striker who needs more pace? Train speed. A midfielder who needs better passing? Focus on that. Strategic training is one of the most powerful tools for improving your squad without spending big on the transfer market.</p>
            </>
          ),
        },
        {
          icon: <TrendingUp className="w-5 h-5" />,
          title: 'Age & Growth Potential',
          content: (
            <>
              <p>Young players (under 23) have higher growth potential and gain more from each training session. Investing in youth development early pays off as these players peak in their mid-twenties with stats far above their starting level.</p>
              <p>Older players (over 30) still benefit from training but gain less per session. Their experience and existing high stats make them reliable now, while younger players represent your squad's future.</p>
            </>
          ),
        },
        {
          icon: <Zap className="w-5 h-5" />,
          title: 'Stat Boosts',
          content: (
            <>
              <p>Booster packs from the store contain temporary stat boosts that you can activate before important matches. A pace boost on your strikers or a defending boost on your centre-backs can swing a tight match in your favour.</p>
              <p>Boosts last for a limited number of matches, so use them strategically — save your best boosts for tournament matches or crucial league encounters where the extra edge matters most.</p>
            </>
          ),
        },
        {
          icon: <Star className="w-5 h-5" />,
          title: 'Overall Rating System',
          content: (
            <>
              <p>Every player has an overall rating calculated from their individual attributes, weighted by position. A goalkeeper's rating depends heavily on reflexes and positioning, while a striker's rating prioritises shooting and pace.</p>
              <p>Training the right stats for each player's position is key to efficiently raising their overall rating. A few targeted training sessions on key attributes can be more effective than spreading training across every stat.</p>
            </>
          ),
        },
      ]}
      faqItems={[
        { q: 'How does player training work?', a: 'Select a player, choose a stat to train (pace, shooting, passing, dribbling, defending, or physical), and pay the training cost in coins. The chosen stat improves immediately.' },
        { q: 'Do younger players train faster?', a: 'Yes. Players under 23 gain more stat points per training session than older players. Investing in youth development early is one of the most cost-effective strategies in the game.' },
        { q: 'What are stat boosts?', a: 'Temporary power-ups from booster packs that increase a player\'s attributes for a limited number of matches. Activate them before big matches for an extra competitive edge.' },
        { q: 'Is there a limit to how much I can train?', a: 'Training is limited by your coin budget. Each session costs coins, and the cost increases with the player\'s current stat level. Higher stats are more expensive to improve.' },
      ]}
      ctaTitle="Develop your squad"
      ctaText="Create your club and start training your players to reach their full potential. Free to play."
    />
  );
}
